import React, { useState, useRef, useEffect } from 'react';
import { toast } from 'react-toastify';
import { HiPaperAirplane } from 'react-icons/hi';
import WysiwygEditor from '../../../Shared/WysiwygEditor';

const CampaignModal = ({ onClose, selectedStudents }) => {
  const [campaignName, setCampaignName] = useState('');
  const [body, setBody] = useState('');
  const modalRef = useRef(null);

  // Handle clicks outside of modal
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [onClose]);

  const handleSendEmail = async () => {
    if (!campaignName.trim()) {
      toast.error('Campaign name cannot be blank.');
      return;
    }
    if (!body.trim()) {
      toast.error('Message cannot be blank.');
      return;
    }

    try {
      await sendEmailToStudents(selectedStudents, campaignName, body);
      toast.success('Campaign sent successfully!');
      onClose();
    } catch (error) {
      console.error('Error sending email:', error);
      toast.error('Failed to send campaign. Please try again.');
    }
  };

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
      <div
        ref={modalRef}
        className="bg-white dark:bg-gray-900 px-8 py-4 rounded-lg shadow-lg w-full max-w-4xl relative overflow-auto"
        style={{ maxHeight: '90vh' }}
      >
        {/* Close button */}
        <button
          type="button"
          className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 dark:text-gray-300 dark:hover:text-gray-100"
          onClick={onClose}
        >
          <span className="sr-only">Close</span>
          &#x2715;
        </button>

        {/* Optional Modal Title */}
        <h2 className="mb-2 text-xl tracking-tight font-semibold text-center text-gray-900 dark:text-white">
          Create Your Email Campaign
        </h2>

        {/* Campaign Name */}
        <div className="mb-4">
          <label
            htmlFor="campaignName"
            className="block mb-2 text-md font-medium text-gray-900 dark:text-gray-300"
          >
            Campaign Name
          </label>
          <input
            id="campaignName"
            type="text"
            className="shadow-sm bg-gray-50 border border-gray-300 text-gray-900 
                       text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 
                       block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 
                       dark:placeholder-gray-400 dark:text-white 
                       dark:focus:ring-blue-500 dark:focus:border-blue-500"
            placeholder="Enter campaign name"
            value={campaignName}
            onChange={(e) => setCampaignName(e.target.value)}
            required
          />
        </div>

        {/* Two columns below */}
        <div className="flex flex-col md:flex-row md:space-x-6">
          {/* Left column: WYSIWYG Editor */}
          <div className="flex-1 w-[50vw] md:mb-0">
            <h3 className="mb-2 text-md font-medium text-gray-900 dark:text-gray-300">
              Message
            </h3>
            <WysiwygEditor onChange={setBody} minHeight="225px" maxHeight="225px" />
          </div>

          {/* Right column: Recipients */}
          <div className="w-1/4">
            <h3 className="text-md mt-2 font-semibold mb-2 text-gray-900 dark:text-white">
              Recipients ({selectedStudents.length})
            </h3>
            <div className="max-h-[287px] overflow-y-auto">
              <ul>
                {selectedStudents.map((student) => (
                  <li key={student.id} className="text-md mb-2">
                    <span className="bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 px-2 py-2 rounded-md block w-full">
                      {student.name}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        {/* Footer Buttons */}
        <div className="flex justify-end mt-2 space-x-2">
          <button
            type="button"
            className="py-2 px-4 text-sm font-medium text-gray-900 bg-gray-200 
                       rounded-lg hover:bg-gray-300 focus:ring-4 focus:outline-none 
                       focus:ring-gray-300 dark:bg-gray-800 dark:text-white 
                       dark:hover:bg-gray-700 dark:focus:ring-gray-600"
            onClick={onClose}
          >
            Cancel
          </button>

          <button
            type="button"
            className={`flex items-center py-2 pr-4 pl-3 text-sm font-medium 
                        rounded-lg focus:ring-4 focus:outline-none
                        ${
                          !campaignName.trim() || !body.trim()
                            ? "bg-gray-300 text-gray-500 cursor-not-allowed dark:bg-gray-700 dark:text-gray-400"
                            : "bg-blue-600 text-white hover:bg-blue-700 focus:ring-blue-300 dark:bg-blue-500 dark:hover:bg-blue-600 dark:focus:ring-blue-400"
                        }`}
            onClick={handleSendEmail}
            disabled={!campaignName.trim() && !body.trim()}
          >
            <HiPaperAirplane className="mr-2 w-4 h-4 transform rotate-90" />
            Send
          </button>
        </div>
      </div>
    </div>
  );
};

// API Call Helper
const sendEmailToStudents = async (students, campaignName, body) => {
  const emails = students.map((student) => student.email);

  const payload = {
    campaignName,
    emails,
    body,
  };

  const response = await fetch('/api/send-email', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    throw new Error('Failed to send email');
  }
};

export default CampaignModal;
